import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { NewsletterPost } from "@/lib/cms-newsletter";
import {
  formatArchiveDate,
  postTimestamp,
  variedReadMinutes,
} from "@/components/newsletter/newsletter-post-utils";

type Props = {
  posts: NewsletterPost[];
  loading?: boolean;
  eyebrow?: string;
  title?: string;
  /** Replaces the default blurb when set (including `null` to hide). */
  description?: ReactNode;
  headingId?: string;
  /**
   * Newsletter post detail: black background + same column width as article (`max-w-3xl`).
   */
  articleColumn?: boolean;
};

export default function NewsletterArchiveList({
  posts,
  loading,
  eyebrow = "All dispatches",
  title = "Browse the archive",
  description,
  headingId = "newsletter-archive-heading",
  articleColumn = false,
}: Props) {
  const intro = description;

  const sectionBg = articleColumn ? "bg-black" : "bg-[#111110]";
  const sectionY = articleColumn
    ? "pt-6 pb-8 sm:pt-8 sm:pb-10"
    : "py-12 sm:py-16";
  const innerWidth = articleColumn
    ? "mx-auto w-[90%] max-w-3xl md:w-[80%]"
    : "mx-auto w-[90%] max-w-[100vw] md:w-[80%]";
  const listDivide = articleColumn ? "divide-white/10" : "divide-[#FFFFFF66]";
  const listTop = articleColumn ? "mt-6 sm:mt-7" : "mt-10";

  if (loading) {
    return (
      <section
        className={`w-full ${sectionBg} ${sectionY} font-fira text-white`}
        aria-hidden
      >
        <div className={innerWidth}>
          <div className="mb-8 h-6 w-48 animate-pulse rounded bg-white/10" />
          <div className="h-10 w-3/4 max-w-md animate-pulse rounded bg-white/10" />
          <div className="mt-10 space-y-8">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded bg-white/[0.06]"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (posts.length === 0) return null;

  return (
    <section
      className={`w-full ${sectionBg} ${sectionY} font-fira text-white`}
      aria-labelledby={headingId}
    >
      <div className={innerWidth}>
        <p className="font-fira text-sm font-normal uppercase tracking-[0.18em] text-[#FFFFFF]">
          {eyebrow}
        </p>
        <h2
          id={headingId}
          className="mt-3 font-fira-condensed text-[32px] font-medium md:font-semibold tracking-tight text-[#FFFFFF] md:text-[42px]"
        >
          {title}
        </h2>
        {intro ? (
          <div className="mt-2 max-w-xl text-[13px] leading-relaxed text-white/55 md:text-[15px]">
            {intro}
          </div>
        ) : null}

        <ul className={`${listTop} divide-y ${listDivide}`} role="list">
          {posts.map((post) => {
            const ts = postTimestamp(post);
            const dateLabel = formatArchiveDate(ts);
            const mins = variedReadMinutes(post);
            return (
              <li key={post.id} role="listitem" className="py-6 first:pt-2">
                <Link
                  to={`/newsletter/${post.slug}`}
                  className="group block text-left transition hover:opacity-90"
                >
                  <p className="text-sm font-fira font-normal text-[#FFFFFFCC]">
                    {dateLabel ? (
                      <time
                        dateTime={
                          ts != null
                            ? new Date(ts * 1000).toISOString()
                            : undefined
                        }
                        className="tabular-nums"
                      >
                        {dateLabel}
                      </time>
                    ) : null}
                    {dateLabel ? (
                      <span className="text-white/35"> &middot; </span>
                    ) : null}
                    <span>{mins} min read</span>
                  </p>
                  <h3 className="mt-2 font-fira text-lg font-medium leading-snug text-[#FFFFFF] md:text-xl">
                    {post.title}
                  </h3>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}